import { FormInstance } from 'antd';
import { Dictionary } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { Account } from 'api/account';
import {
  RateCard,
  RateCardLite,
  RateCardVersion,
  RoleRate,
} from 'api/rate-cards';
import { confirm } from 'components/atoms/modal';
import { useTranslation } from 'hooks/use-translation';
import { defaultFormat } from 'utils/date-field/formatter';
import { negativeUid } from 'utils/uid';
import { RateCardCreationMeta } from '../types';

interface Props {
  account: Account;
  roleRates: Dictionary<RoleRate>;
  form: FormInstance<RateCardCreationMeta>;
  selectedRateCard?: RateCardLite;
  selectedRateCardVersion?: RateCardVersion;
  rateCardDetails?: RateCard;
  rateCardVersions: RateCardVersion[];
  onUpdateRateCardVersion: (newVersionId: number) => void;
  onDrawerClose: () => void;
}

interface ReturnProps {
  isCurrentRateCardVersionReadOnly: boolean;
  isUnsavedRateCardVersionPresent: boolean;
  createNewRateCardVersion: (
    versionToCopy?: RateCardVersion,
  ) => RateCardVersion;
  getRateCardVersionPayload: () => RateCardVersion;
  getRateCardPayload: () => RateCard;
  getActiveOrRecentRateCardVersion: () => RateCardVersion | undefined;
  updateSelectedRateCardVersion: (newVersion: number) => void;
  closeDrawerHandler: () => void;
}

export const useRateCardUtils = ({
  account,
  roleRates,
  form,
  selectedRateCard,
  selectedRateCardVersion,
  rateCardDetails,
  rateCardVersions,
  onUpdateRateCardVersion,
  onDrawerClose,
}: Props): ReturnProps => {
  const closeConfirmTitle = useTranslation(
    'settings.rate_cards.drawer.close.confirmation.title',
  );
  const closeConfirmContent = useTranslation(
    'settings.rate_cards.drawer.close.confirmation.content',
  );
  const closeConfirmCta = useTranslation(
    'settings.rate_cards.drawer.close.confirmation.cta.primary',
  );
  const closeCancelCta = useTranslation(
    'settings.rate_cards.drawer.close.confirmation.cta.secondary',
  );

  const isCurrentRateCardVersionReadOnly = useMemo(() => {
    if (
      selectedRateCardVersion == null ||
      selectedRateCardVersion.rateCardVersionId < 0
    ) {
      return false;
    }

    return (
      (selectedRateCard != null &&
        selectedRateCardVersion.rateCardVersionId > 0 &&
        moment(selectedRateCardVersion.effectiveDate)
          .startOf('day')
          .isSameOrBefore(moment().startOf('day'))) || // should account for timezone here
      !!selectedRateCardVersion.active
    );
  }, [selectedRateCard, selectedRateCardVersion]);

  const isUnsavedRateCardVersionPresent = useMemo(
    () =>
      Boolean(
        rateCardVersions.find(({ rateCardVersionId }) => rateCardVersionId < 0),
      ),
    [rateCardVersions],
  );

  const createNewRateCardVersion = useCallback(
    (versionToCopy?: RateCardVersion): RateCardVersion => {
      const newVersion = {
        rateCardVersionId: negativeUid(),
        effectiveDate: defaultFormat(moment().startOf('day')),
        ...(versionToCopy != null
          ? {
              rateCardId: versionToCopy.rateCardId,
              accountId: versionToCopy.rateCardId,
              defaultRate: versionToCopy.defaultRate,
              roleBillRateMapping: versionToCopy.roleBillRateMapping,
            }
          : {
              rateCardId: selectedRateCard?.rateCardId ?? negativeUid(),
              accountId:
                selectedRateCard?.accountId ?? (account.accountId as number),
              defaultRate: {
                isDefault: true,
                multiCurrencyRates: [
                  {
                    currency: account.currency,
                    billRate: 0,
                  },
                ],
              },
              roleBillRateMapping: [],
            }),
      };

      return newVersion;
    },
    [
      account.accountId,
      account.currency,
      selectedRateCard?.accountId,
      selectedRateCard?.rateCardId,
    ],
  );

  const getRateCardVersionPayload = useCallback((): RateCardVersion => {
    const { effectiveDate } = form.getFieldsValue();

    const rateCardId = selectedRateCard?.rateCardId ?? negativeUid();
    const rateCardVersionId =
      selectedRateCardVersion?.rateCardVersionId ?? negativeUid();
    const { [-1]: defaultRatePayload, ...roleRatesPayload } = roleRates;

    return {
      accountId: account.accountId,
      defaultRate: {
        isDefault: true,
        multiCurrencyRates: Object.keys(
          defaultRatePayload.multiCurrencyRates,
        ).map((currency) => ({
          ...defaultRatePayload.multiCurrencyRates[currency],
        })),
      },
      effectiveDate: defaultFormat(effectiveDate),
      rateCardId,
      rateCardVersionId,
      roleBillRateMapping: Object.keys(roleRatesPayload).map((roleId) => ({
        roleId: parseInt(roleId, 10),
        multiCurrencyRates: Object.keys(
          roleRatesPayload[roleId].multiCurrencyRates,
        ).map((currency) => ({
          ...roleRatesPayload[roleId].multiCurrencyRates[currency],
        })),
      })),
    };
  }, [
    form,
    selectedRateCard,
    selectedRateCardVersion,
    roleRates,
    account.accountId,
  ]);

  const getRateCardPayload = useCallback((): RateCard => {
    const { rateCardName: name } = form.getFieldsValue();

    const rateCardId = selectedRateCard?.rateCardId ?? negativeUid();
    const rateCardName = selectedRateCard?.rateCardName ?? name;

    return {
      rateCardId,
      rateCardName,
      accountId: account.accountId,
      enabled: true,
      rateCardVersions: [getRateCardVersionPayload()],
    };
  }, [account.accountId, form, getRateCardVersionPayload, selectedRateCard]);

  const getActiveOrRecentRateCardVersion = useCallback(
    () =>
      rateCardDetails != null
        ? rateCardDetails.rateCardVersions.find((version) => version.active) ??
          rateCardDetails.rateCardVersions[0]
        : undefined,
    [rateCardDetails],
  );

  const updateSelectedRateCardVersion = useCallback(
    (newVersion: number) => {
      if (selectedRateCardVersion == null) {
        return;
      }

      if (isUnsavedRateCardVersionPresent) {
        confirm({
          title: closeConfirmTitle,
          content: closeConfirmContent,
          okText: closeConfirmCta,
          cancelText: closeCancelCta,
          okType: 'danger',
          onOk: () => {
            onUpdateRateCardVersion(newVersion);
          },
        });
      } else {
        onUpdateRateCardVersion(newVersion);
      }
    },
    [
      closeCancelCta,
      closeConfirmContent,
      closeConfirmCta,
      closeConfirmTitle,
      isUnsavedRateCardVersionPresent,
      onUpdateRateCardVersion,
      selectedRateCardVersion,
    ],
  );

  const closeDrawerHandler = useCallback(() => {
    if (isUnsavedRateCardVersionPresent && rateCardDetails != null) {
      confirm({
        title: closeConfirmTitle,
        content: closeConfirmContent,
        okText: closeConfirmCta,
        cancelText: closeCancelCta,
        okType: 'danger',
        onOk: onDrawerClose,
      });
    } else {
      onDrawerClose();
    }
  }, [
    closeCancelCta,
    closeConfirmContent,
    closeConfirmCta,
    closeConfirmTitle,
    isUnsavedRateCardVersionPresent,
    onDrawerClose,
    rateCardDetails,
  ]);

  return {
    isCurrentRateCardVersionReadOnly,
    isUnsavedRateCardVersionPresent,
    createNewRateCardVersion,
    getRateCardVersionPayload,
    getRateCardPayload,
    getActiveOrRecentRateCardVersion,
    updateSelectedRateCardVersion,
    closeDrawerHandler,
  };
};
